/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file TeamChatContext.js
 * @description
 * App-wide state of the Team Chat module for the CURRENTLY SELECTED STORE:
 * bootstrap (channels, unread/mention totals, permissions, credits, meeting
 * availability), one Socket.IO connection (`user:<id>` room) kept in sync with
 * the channel list, and a tiny pub/sub so open chat screens receive the raw
 * socket events without the provider holding message lists.
 *
 * Mounted once in AppProviders (next to MailProvider) so every app mode
 * (Management / POS / Service / Warehouse / Sales) can show the unread badge.
 *
 * Store scope: the account's active store, unless the user picked another of
 * their stores in the in-chat store switcher (`chatStoreScope.js`) — chats are
 * per store, and an account that belongs to several stores can be invited to
 * channels of any of them.
 *
 * Isolation rules:
 *  - keyed on `restaurantId + userId`: a store switch or a different user
 *    resets the state, closes the socket and ignores in-flight responses
 *    (generation counter) — nothing leaks across stores;
 *  - logout runs `restartApp`, which remounts the whole tree (fresh provider);
 *  - the socket only opens after a successful bootstrap, so a store without
 *    the module (403) or a backend without the endpoints (404) never holds a
 *    permanent connection.
 *
 * Never blocks first paint: the bootstrap is deferred behind
 * `InteractionManager.runAfterInteractions`. Only small objects are kept
 * (channel list + counters) — message lists live in the screens.
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AppState, InteractionManager} from 'react-native';
import {useSelector} from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {getAccessToken, getUserInfo} from '../storage';
import {isFeatureEnabled} from '../../config/featureFlags';
import {connectTeamChatSocket} from '../TeamChatSocket';
import {
    getChatBootstrap,
    heartbeatChatPresence,
    isChatForbiddenError,
    isChatUnavailableError,
    listChatStores,
    updateChatMyPreferences,
} from '../../services/teamChatApi';
import {
    arr,
    computeUnreadTotals,
    idOf,
    mentionsUser,
    normalizeChannel,
    textOf,
} from './chatShapes';
import {mergeChatPreferences, selectChatPreferences} from './chatPreferences';
import {isTeamChatPilotStore} from './teamChatPilot';
import {normalizeChatStore, pickChatStore, readChatStoreChoice, writeChatStoreChoice} from './chatStoreScope';
import {SystemContext} from '../SystemContext';

/** Build-time feature flag of the module (see config/featureFlags.js). */
export const TEAM_CHAT_FEATURE_KEY = 'function.teamChat';

/** Poll cadence while the socket is down (ms). */
const OFFLINE_POLL_MS = 60 * 1000;
/** Minimum spacing between silent foreground refreshes (ms). */
const FOREGROUND_REFRESH_MIN_MS = 30 * 1000;
/** Client → server typing throttle per channel (ms). */
const TYPING_THROTTLE_MS = 3000;
/** Keep previews short — the list never needs the full text. */
const PREVIEW_MAX = 140;

/**
 * @typedef {'disabled'|'idle'|'loading'|'ready'|'unavailable'|'forbidden'|'error'} TeamChatStatus
 */

const TeamChatContext = createContext(null);

const NOOP = () => undefined;
const NOOP_UNSUB = () => NOOP;

/** Context value used when no provider is mounted (tests, isolated screens). */
const FALLBACK_VALUE = {
    enabled: false,
    status: 'disabled',
    available: false,
    visible: false,
    connected: false,
    restaurantId: null,
    userId: null,
    stores: [],
    canSwitchStore: false,
    switchStore: async () => undefined,
    me: null,
    channels: [],
    unreadTotal: 0,
    mentionTotal: 0,
    permissions: {},
    credits: null,
    meetingConfigured: false,
    settings: null,
    preferences: {meetingTranscript: true},
    updateMyPreferences: async () => ({meetingTranscript: true}),
    refresh: async () => null,
    upsertChannel: NOOP,
    removeChannel: NOOP,
    markChannelReadLocal: NOOP,
    setActiveChannel: NOOP,
    subscribe: NOOP_UNSUB,
    emitTyping: NOOP,
    getChannel: () => null,
    isOnline: () => undefined,
};

/** Presence heartbeat cadence (backend counts a user online for 75 s after a beat). */
const PRESENCE_HEARTBEAT_MS = 30 * 1000;

/** Back-off after a failed presence heartbeat (old backend / network down). */
const PRESENCE_RETRY_MS = 5 * 60 * 1000;

/**
 * Normalise a `POST /api/chat/presence` snapshot into `{available, users: {id: online}}`.
 * @param {Object} payload
 * @returns {{available: boolean, users: Object<string, boolean>}}
 */
const toPresenceState = (payload) => {
    const users = {};
    arr(payload?.users).forEach((entry) => {
        const id = idOf(entry?.userId);
        if (id) {
            users[id] = !!entry.online;
        }
    });

    return {available: !!payload?.available, users};
};

/**
 * Redux `device.restaurant` (null-safe). Nothing dispatches `setRestaurant` in
 * this app today, so it is normally null — kept as the first source in case a
 * flow starts populating it.
 * @param {Object} state
 * @returns {Object|null}
 */
const selectReduxStore = (state) => state?.device?.restaurant || null;

/**
 * Read the persisted active store + place (AsyncStorage `restaurant` /
 * `restaurantPlace`) — the same keys SystemContext hydrates from.
 * @returns {Promise<{store: Object|null, place: Object|null}>}
 */
const readStoredScope = async () => {
    try {
        const [[, store], [, place]] = await AsyncStorage.multiGet(['restaurant', 'restaurantPlace']);

        return {store: store ? JSON.parse(store) : null, place: place ? JSON.parse(place) : null};
    } catch (_) {
        return {store: null, place: null};
    }
};

/**
 * Sort channels: last activity first.
 * @param {Array} channels
 * @returns {Array}
 */
const sortChannels = (channels) => arr(channels).slice().sort((a, b) => {
    const aTime = new Date(a?.lastMessageAt || a?.lastMessagePreview?.at || a?.createdAt || 0).getTime() || 0;
    const bTime = new Date(b?.lastMessageAt || b?.lastMessagePreview?.at || b?.createdAt || 0).getTime() || 0;

    return bTime - aTime;
});

/**
 * Provider — see the file header.
 * @param {{children: React.ReactNode}} props
 * @returns {JSX.Element}
 */
export const TeamChatProvider = ({children}) => {
    // Active store: redux → SystemContext (AsyncStorage-hydrated) → direct storage
    // read. Gating on redux alone hid chat everywhere (it is never populated).
    const system = useContext(SystemContext);
    const reduxStore = useSelector(selectReduxStore);
    const [storedScope, setStoredScope] = useState({store: null, place: null});
    const activeStore = reduxStore || system?.restaurant || storedScope.store || null;
    const activePlace = system?.restaurantPlace || storedScope.place || null;
    // Any known store; the backend rollout (403 → 'forbidden') hides the rest.
    const pilotStore = isTeamChatPilotStore(activeStore) || isTeamChatPilotStore(activePlace);
    const enabled = isFeatureEnabled(TEAM_CHAT_FEATURE_KEY) && pilotStore;
    const reduxRestaurantId = idOf(activeStore) || null;
    const hasStore = !!(reduxStore || system?.restaurant || storedScope.store || storedScope.place);

    // No store in redux/SystemContext yet (boot, fresh login): poll the persisted
    // keys cheaply until one appears.
    useEffect(() => {
        if (hasStore) {
            return undefined;
        }
        let cancelled = false;
        const read = () => readStoredScope().then((next) => {
            if (!cancelled && (next.store || next.place)) {
                setStoredScope((previous) => (idOf(previous.store) === idOf(next.store) && idOf(previous.place) === idOf(next.place)
                    ? previous
                    : next));
            }
        });
        read();
        const timer = globalThis.setInterval(read, 5000);

        return () => {
            cancelled = true;
            globalThis.clearInterval(timer);
        };
    }, [hasStore]);
    const [scope, setScope] = useState({restaurantId: null, userId: null});
    const [stores, setStores] = useState([]);
    const [status, setStatus] = useState(enabled ? 'idle' : 'disabled');
    const [channels, setChannels] = useState([]);
    const [boot, setBoot] = useState(null);
    const [connected, setConnected] = useState(false);

    const mountedRef = useRef(true);
    const generationRef = useRef(0);
    const activeChannelRef = useRef(null);
    const listenersRef = useRef(new Set());
    const socketRef = useRef(null);
    const lastRefreshRef = useRef(0);
    const typingSentRef = useRef({});
    const missingRefreshTimerRef = useRef(null);
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const channelsRef = useRef(channels);
    channelsRef.current = channels;

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    /* ── Resolve the store + user scope (redux first, persisted fallback) ── */
    const reduxRestaurantIdRef = useRef(reduxRestaurantId);
    reduxRestaurantIdRef.current = reduxRestaurantId;
    /** The account's active store (redux/SystemContext first, persisted `restaurant` fallback). */
    const resolveBaseStoreId = useCallback(async () => {
        let baseId = reduxRestaurantIdRef.current;
        if (!baseId) {
            try {
                const raw = await AsyncStorage.getItem('restaurant');
                baseId = raw ? idOf(JSON.parse(raw)) || null : null;
            } catch (_) {
                baseId = null;
            }
        }

        return baseId || null;
    }, []);

    const resolveScope = useCallback(async () => {
        if (!enabled) {
            return;
        }
        let restaurantId = null;
        let userId = null;
        try {
            const token = await getAccessToken();
            if (token) {
                userId = idOf(await getUserInfo()) || null;
            }
        } catch (_) {
            // storage failure → stay idle
        }
        restaurantId = await resolveBaseStoreId();
        // The store picked in the in-chat switcher wins while the active store is unchanged.
        restaurantId = pickChatStore(await readChatStoreChoice(), restaurantId) || restaurantId;
        if (mountedRef.current) {
            setScope((previous) => (previous.restaurantId === restaurantId && previous.userId === userId
                ? previous
                : {restaurantId, userId}));
        }
    }, [enabled, resolveBaseStoreId]);

    useEffect(() => {
        resolveScope();
    }, [resolveScope, reduxRestaurantId]);

    /**
     * Point the chat at another of the user's stores (or back at the active one) —
     * the app's active store is never touched.
     * @param {string} storeId
     * @returns {Promise<void>}
     */
    const switchStore = useCallback(async (storeId) => {
        const baseId = await resolveBaseStoreId();
        const target = idOf(storeId);
        await writeChatStoreChoice(baseId && target && target !== baseId ? {base: baseId, store: target} : null);
        await resolveScope();
    }, [resolveBaseStoreId, resolveScope]);

    /** Stores the user can chat in + their unread totals (switcher feed; failures keep the last list). */
    const loadStores = useCallback(async () => {
        try {
            const data = await listChatStores();
            if (mountedRef.current) {
                setStores(arr(data?.items).map(normalizeChatStore).filter(Boolean));
            }
        } catch (_) {
            // old backend / offline → no switcher, the chat itself keeps working
        }
    }, []);

    // Signed out / no store yet (login screen): re-check cheaply until both exist —
    // a fresh login may keep the same persisted store id, so redux alone would not
    // re-trigger the resolution.
    useEffect(() => {
        if (!enabled || (scope.restaurantId && scope.userId)) {
            return undefined;
        }
        const timer = globalThis.setInterval(resolveScope, 5000);

        return () => globalThis.clearInterval(timer);
    }, [enabled, scope, resolveScope]);

    /**
     * (Re)load the bootstrap of the current scope.
     * @param {{silent?: boolean}} [options]
     * @returns {Promise<Object|null>}
     */
    const refresh = useCallback(async ({silent = false} = {}) => {
        const {restaurantId, userId} = scopeRef.current;
        if (!enabled || !restaurantId || !userId) {
            return null;
        }
        const generation = generationRef.current;
        lastRefreshRef.current = Date.now();
        loadStores();
        if (!silent) {
            setStatus((previous) => (previous === 'ready' ? previous : 'loading'));
        }
        try {
            const data = await getChatBootstrap(restaurantId);
            if (!mountedRef.current || generation !== generationRef.current) {
                return null;
            }
            const list = sortChannels(arr(data?.channels).map(normalizeChannel).filter(Boolean));
            setChannels(list);
            setBoot({
                me: data?.me || null,
                settings: data?.settings || null,
                credits: data?.credits || null,
                meetingConfigured: !!data?.meetingConfigured,
                permissions: data?.permissions && typeof data.permissions === 'object' ? data.permissions : {},
            });
            setStatus('ready');

            return data;
        } catch (error) {
            if (!mountedRef.current || generation !== generationRef.current) {
                return null;
            }
            if (isChatForbiddenError(error)) {
                setStatus('forbidden');
            } else if (isChatUnavailableError(error)) {
                setStatus('unavailable');
            } else if (error?.code === 'AUTH_REQUIRED') {
                setStatus('idle');
            } else {
                // Keep a working list on a transient failure (offline etc.).
                setStatus((previous) => (previous === 'ready' ? previous : 'error'));
            }

            return null;
        }
    }, [enabled, loadStores]);

    /* ── Scope change → reset everything, then bootstrap (deferred) ── */
    useEffect(() => {
        generationRef.current += 1;
        setChannels([]);
        setBoot(null);
        setConnected(false);
        activeChannelRef.current = null;
        typingSentRef.current = {};
        if (!enabled) {
            setStatus('disabled');

            return undefined;
        }
        if (!scope.restaurantId || !scope.userId) {
            setStatus('idle');

            return undefined;
        }
        // New store/user: never keep the previous scope's 'ready' (and its socket).
        setStatus('loading');
        const task = InteractionManager.runAfterInteractions(() => {
            refresh();
        });

        return () => task?.cancel?.();
    }, [enabled, scope, refresh]);

    /** Fan an event out to the screen listeners. */
    const notify = useCallback((eventName, payload) => {
        listenersRef.current.forEach((listener) => {
            try {
                listener(eventName, payload);
            } catch (_) {
                // a broken screen listener must not break the others
            }
        });
    }, []);

    /** Debounced refresh used when an event references an unknown channel. */
    const scheduleMissingRefresh = useCallback(() => {
        if (missingRefreshTimerRef.current) {
            return;
        }
        missingRefreshTimerRef.current = globalThis.setTimeout(() => {
            missingRefreshTimerRef.current = null;
            refresh({silent: true});
        }, 1200);
    }, [refresh]);

    useEffect(() => () => {
        if (missingRefreshTimerRef.current) {
            globalThis.clearTimeout(missingRefreshTimerRef.current);
            missingRefreshTimerRef.current = null;
        }
    }, []);

    /** Apply a socket event to the channel list. */
    const applyEvent = useCallback((eventName, payload) => {
        const meId = scopeRef.current.userId;
        switch (eventName) {
            case 'chat:message': {
                const channelId = idOf(payload?.channelId || payload?.message?.channel);
                const message = payload?.message;
                if (!channelId || !message) {
                    return;
                }
                const fromMe = idOf(message.sender) === String(meId || '');
                const isReply = !!idOf(message.threadRoot);
                const mentioned = mentionsUser(message, meId);
                if (!channelsRef.current.some((channel) => channel._id === channelId)) {
                    // A brand-new DM / channel I was just added to — reload the list.
                    scheduleMissingRefresh();

                    return;
                }
                setChannels((previous) => sortChannels(previous.map((channel) => {
                    if (channel._id !== channelId) {
                        return channel;
                    }
                    const next = {...channel};
                    if (!isReply) {
                        next.lastMessageAt = message.createdAt || new Date().toISOString();
                        next.lastMessagePreview = {
                            text: textOf(message.text).slice(0, PREVIEW_MAX),
                            senderName: textOf(message.sender),
                            at: next.lastMessageAt,
                        };
                    }
                    const isActive = activeChannelRef.current === channelId;
                    if (!fromMe && !isActive && next.me && (!isReply || mentioned)) {
                        next.me = {
                            ...next.me,
                            unreadCount: (Number(next.me.unreadCount) || 0) + 1,
                            mentionCount: (Number(next.me.mentionCount) || 0) + (mentioned ? 1 : 0),
                        };
                    }

                    return next;
                })));
                break;
            }
            case 'chat:channel:updated': {
                const channel = normalizeChannel(payload?.channel);
                if (!channel) {
                    return;
                }
                setChannels((previous) => {
                    const index = previous.findIndex((item) => item._id === channel._id);
                    if (index < 0) {
                        return sortChannels([channel, ...previous]);
                    }
                    const merged = previous.slice();
                    // Keep my per-user counters when the broadcast omits them.
                    merged[index] = {...previous[index], ...channel, me: channel.me || previous[index].me};

                    return sortChannels(merged);
                });
                break;
            }
            case 'chat:channel:removed': {
                const channelId = idOf(payload?.channelId);
                if (channelId) {
                    setChannels((previous) => previous.filter((item) => item._id !== channelId));
                }
                break;
            }
            case 'chat:read': {
                const channelId = idOf(payload?.channelId);
                // Another device of mine read the channel → clear the badge here too.
                if (channelId && idOf(payload?.userId) === String(meId || '')) {
                    setChannels((previous) => previous.map((item) => (item._id === channelId && item.me
                        ? {...item, me: {...item.me, unreadCount: 0, mentionCount: 0, lastReadMessageId: payload?.lastReadMessageId || item.me.lastReadMessageId}}
                        : item)));
                }
                break;
            }
            default:
                break;
        }
    }, [scheduleMissingRefresh]);

    /* ── Socket: one connection while chat is ready for this scope ── */
    // `isReady` flips loading→ready once per scope; silent refreshes keep it true,
    // so the connection is not recycled on every bootstrap reload.
    const isReady = status === 'ready';
    useEffect(() => {
        if (!isReady || !scope.userId || !scope.restaurantId) {
            return undefined;
        }
        const generation = generationRef.current;
        const connection = connectTeamChatSocket({
            userId: scope.userId,
            onEvent: (eventName, payload) => {
                if (generation !== generationRef.current) {
                    return;
                }
                // Drop events of another store (multi-store users share one user room).
                const eventRestaurant = idOf(payload?.restaurantId || payload?.message?.restaurant || payload?.channel?.restaurant || payload?.meeting?.restaurant);
                if (eventRestaurant && eventRestaurant !== scope.restaurantId) {
                    return;
                }
                applyEvent(eventName, payload);
                notify(eventName, payload);
            },
            onStatus: (isConnected, {reconnect}) => {
                if (generation !== generationRef.current || !mountedRef.current) {
                    return;
                }
                setConnected(isConnected);
                if (isConnected && reconnect) {
                    // Reconcile whatever was missed while offline.
                    refresh({silent: true});
                    notify('chat:reconnected', {});
                }
            },
        });
        socketRef.current = connection;

        return () => {
            connection.close();
            if (socketRef.current === connection) {
                socketRef.current = null;
            }
        };
    }, [isReady, scope, applyEvent, notify, refresh]);

    /* ── Foreground refresh + offline poll fallback ── */
    useEffect(() => {
        if (!enabled || !scope.restaurantId || !scope.userId) {
            return undefined;
        }
        const sub = AppState.addEventListener('change', (state) => {
            if (state === 'active' && Date.now() - lastRefreshRef.current > FOREGROUND_REFRESH_MIN_MS) {
                refresh({silent: true});
            }
        });

        return () => sub?.remove?.();
    }, [enabled, scope, refresh]);

    useEffect(() => {
        if (status !== 'ready' || connected) {
            return undefined;
        }
        const timer = globalThis.setInterval(() => {
            if (AppState.currentState === 'active') {
                refresh({silent: true});
            }
        }, OFFLINE_POLL_MS);

        return () => globalThis.clearInterval(timer);
    }, [status, connected, refresh]);

    /* ── Presence heartbeat (green / grey avatar dot) ── */
    const [presence, setPresence] = useState({available: false, users: {}});
    useEffect(() => {
        setPresence({available: false, users: {}});
        if (status !== 'ready' || !scope.restaurantId || !scope.userId) {
            return undefined;
        }
        let cancelled = false;
        let retryAt = 0;
        const beat = async () => {
            if (AppState.currentState !== 'active' || Date.now() < retryAt) {
                return;
            }
            try {
                const payload = await heartbeatChatPresence(scope.restaurantId);
                if (!cancelled && mountedRef.current) {
                    setPresence(toPresenceState(payload));
                }
            } catch (_) {
                retryAt = Date.now() + PRESENCE_RETRY_MS;
            }
        };
        beat();
        const timer = globalThis.setInterval(beat, PRESENCE_HEARTBEAT_MS);
        const sub = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
                beat();
            }
        });

        return () => {
            cancelled = true;
            globalThis.clearInterval(timer);
            sub?.remove?.();
        };
    }, [status, scope]);

    const isOnline = useCallback((userId) => {
        const id = idOf(userId);
        if (!id || !presence.available) {
            return undefined;
        }

        return !!presence.users[id];
    }, [presence]);

    /* ── Imperative helpers exposed to screens ── */

    const upsertChannel = useCallback((raw) => {
        const channel = normalizeChannel(raw);
        if (!channel) {
            return;
        }
        setChannels((previous) => {
            const index = previous.findIndex((item) => item._id === channel._id);
            if (index < 0) {
                return sortChannels([channel, ...previous]);
            }
            const merged = previous.slice();
            merged[index] = {...previous[index], ...channel, me: channel.me || previous[index].me};

            return sortChannels(merged);
        });
    }, []);

    const removeChannel = useCallback((channelId) => {
        const id = idOf(channelId);
        setChannels((previous) => previous.filter((item) => item._id !== id));
    }, []);

    const markChannelReadLocal = useCallback((channelId, lastReadMessageId) => {
        const id = idOf(channelId);
        setChannels((previous) => previous.map((item) => (item._id === id && item.me
            && (item.me.unreadCount || item.me.mentionCount || lastReadMessageId)
            ? {...item, me: {...item.me, unreadCount: 0, mentionCount: 0, lastReadMessageId: lastReadMessageId || item.me.lastReadMessageId}}
            : item)));
    }, []);

    const setActiveChannel = useCallback((channelId) => {
        activeChannelRef.current = channelId ? idOf(channelId) : null;
    }, []);

    const subscribe = useCallback((listener) => {
        if (typeof listener !== 'function') {
            return NOOP;
        }
        listenersRef.current.add(listener);

        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    const emitTyping = useCallback((channelId) => {
        const id = idOf(channelId);
        if (!id) {
            return;
        }
        const now = Date.now();
        if (now - (typingSentRef.current[id] || 0) < TYPING_THROTTLE_MS) {
            return;
        }
        typingSentRef.current[id] = now;
        socketRef.current?.emit?.('chat:typing', {channelId: id});
    }, []);

    const getChannel = useCallback((channelId) => {
        const id = idOf(channelId);

        return channelsRef.current.find((item) => item._id === id) || null;
    }, []);

    const totals = useMemo(() => computeUnreadTotals(channels), [channels]);

    const preferences = useMemo(() => selectChatPreferences(boot?.me), [boot?.me]);

    const bootRef = useRef(boot);
    bootRef.current = boot;

    /**
     * Patch the caller's personal preferences optimistically; roll back and
     * rethrow on error (callers surface it through `alertChatError`).
     * @param {{meetingTranscript?: boolean}} patch
     * @returns {Promise<{meetingTranscript: boolean}>} Server-confirmed preferences.
     */
    const updateMyPreferences = useCallback(async (patch = {}) => {
        const generation = generationRef.current;
        const previous = selectChatPreferences(bootRef.current?.me);
        const write = (next) => setBoot((prev) => (prev ? {...prev, me: mergeChatPreferences(prev.me, next)} : prev));

        write(patch);
        try {
            const restaurantId = scopeRef.current.restaurantId;
            const saved = await updateChatMyPreferences(restaurantId ? {...patch, restaurantId} : patch);
            if (mountedRef.current && generation === generationRef.current) {
                if (typeof saved?.meetingTranscript === 'boolean') {
                    write({meetingTranscript: saved.meetingTranscript});
                }
                // Additive: newer backends return the effective auto-transcript status.
                if (saved?.autoTranscribe && typeof saved.autoTranscribe === 'object') {
                    setBoot((prev) => (prev ? {...prev, me: {...(prev.me || {}), autoTranscribe: saved.autoTranscribe}} : prev));
                }
            }

            return selectChatPreferences({preferences: saved});
        } catch (error) {
            if (mountedRef.current && generation === generationRef.current) {
                write(previous);
            }
            throw error;
        }
    }, []);

    const value = useMemo(() => ({
        enabled,
        status,
        available: status === 'ready',
        // Entry points stay visible unless the build or the store/role excludes chat;
        // a backend without the endpoints shows the friendly "not available yet" page.
        // …and a refused store keeps them visible when the user can chat in another one.
        visible: enabled && status !== 'disabled'
            && (status !== 'forbidden' || stores.some((store) => store.enabled && store.restaurantId !== scope.restaurantId)),
        connected,
        restaurantId: scope.restaurantId,
        userId: scope.userId,
        stores,
        canSwitchStore: stores.length > 1,
        switchStore,
        me: boot?.me || null,
        channels,
        unreadTotal: totals.unreadTotal,
        mentionTotal: totals.mentionTotal,
        permissions: boot?.permissions || {},
        credits: boot?.credits || null,
        meetingConfigured: !!boot?.meetingConfigured,
        settings: boot?.settings || null,
        preferences,
        updateMyPreferences,
        refresh,
        upsertChannel,
        removeChannel,
        markChannelReadLocal,
        setActiveChannel,
        subscribe,
        emitTyping,
        getChannel,
        isOnline,
    }), [enabled, status, connected, scope, stores, switchStore, boot, channels, totals, refresh, upsertChannel, removeChannel,
        markChannelReadLocal, setActiveChannel, subscribe, emitTyping, getChannel, isOnline, preferences, updateMyPreferences]);

    return <TeamChatContext.Provider value={value}>{children}</TeamChatContext.Provider>;
};

/**
 * Read the team chat state (safe fallback when no provider is mounted).
 * @returns {typeof FALLBACK_VALUE}
 */
export const useTeamChat = () => useContext(TeamChatContext) || FALLBACK_VALUE;

export default TeamChatContext;
